import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { createSavedSearch, updateSavedSearch } from '../../../util/api';
import { NamedLink } from '../../../components';
import {
  EDIT_PARAM,
  SAVE_MARKER,
  hasSearchCriteria,
} from '../../SavedSearchesPage/SavedSearchesPage.shared';
import { markSearchSaved } from '../../SavedSearchesPage/SavedSearchesPage.duck';

import css from './SaveSearchButton.module.css';

/** Params that describe the view, not the search. Never part of a bevakning. */
const VIEW_PARAMS = ['page', 'mapSearch', SAVE_MARKER, EDIT_PARAM];

/**
 * Which URL the post-login auto-save has already been claimed for.
 *
 * The search page renders this button three times (quick-search card, its
 * hidden mobile twin, and the results toolbar), and they all see the same
 * marker in the URL. Without a claim shared between them, every instance fires
 * its own save on the way back from login: one succeeds and the rest come back
 * 409 duplicate. The unique index means no bad data, but it is five pointless
 * round trips and five red lines in the console.
 *
 * Module scope is what makes it shared. It is released below as soon as the
 * marker leaves the URL, so a later round trip for the same search still works.
 */
let autoSaveClaim = null;

/**
 * "Bevaka denna sökning" — saves the current search as a bevakning.
 *
 * Rendered in three places, all sharing this one implementation: the quick
 * search card, the results toolbar, and the no-results block.
 *
 * Note for anyone tracing history here: the no-results version used to write
 * into the `savedQueries` collection, which is the "Dina bilar" saved-cars
 * feature. Saved searches now have their own collection and API — see
 * P25009-371. The two must not be mixed again.
 */
/* The bell in the results toolbar's "Bevaka" (P25009-491). */
const BellIcon = () => (
  <svg
    className={css.bell}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
  </svg>
);

const SaveSearchButton = props => {
  /* `withIcon`: a bell before the label; `iconOnly`: the bell alone, the label
   * kept for screen readers (the phone toolbar, P25009-491). */
  const { className, label, withIcon, iconOnly } = props;

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(state => !!state.user?.currentUser);
  const savedParams = useSelector(state => state.SavedSearchesPage?.savedParams || []);

  // idle | saving | saved | duplicate | limit | error
  const [status, setStatus] = useState('idle');

  /**
   * Saving navigates: clearMarkers() rewrites the URL, and the user may click a
   * result while the request is still in the air. Either can unmount this
   * button before the response lands, so the state setters below are guarded —
   * without this React warns about updating an unmounted component.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const setStatusIfMounted = next => {
    if (mounted.current) setStatus(next);
  };

  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get(EDIT_PARAM);
  const wantsAutoSave = searchParams.has(SAVE_MARKER);

  /** The query string a bevakning stores: the search, minus view state. */
  const paramsToSave = useCallback(() => {
    const next = new URLSearchParams(location.search);
    VIEW_PARAMS.forEach(key => next.delete(key));
    const qs = next.toString();
    return qs ? `?${qs}` : '';
  }, [location.search]);

  /** Drop the one-shot markers so a refresh does not save twice. */
  const clearMarkers = useCallback(() => {
    // If the save finished after the user navigated away, rewriting the URL
    // would yank them back to the search page. Their move wins.
    if (!mounted.current) return;

    const next = new URLSearchParams(location.search);
    if (!next.has(SAVE_MARKER) && !next.has(EDIT_PARAM)) return;
    next.delete(SAVE_MARKER);
    next.delete(EDIT_PARAM);
    const qs = next.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' });
  }, [history, location.pathname, location.search]);

  const save = useCallback(async () => {
    const params = paramsToSave();

    if (!params) {
      setStatusIfMounted('error');
      return;
    }

    setStatusIfMounted('saving');

    try {
      if (editId) {
        await updateSavedSearch(editId, { params });
      } else {
        await createSavedSearch({ params });
      }
      dispatch(markSearchSaved(params));
      setStatusIfMounted('saved');
      clearMarkers();
    } catch (e) {
      // The server reports why in `reason`, so the button can say something
      // specific instead of a generic failure.
      const reason = e?.reason;
      // "Already saved" is still saved — record it so every copy of the button
      // agrees, and so a retry does not ask the server again.
      if (reason === 'duplicate') dispatch(markSearchSaved(params));
      setStatusIfMounted(
        reason === 'duplicate' ? 'duplicate' : reason === 'limit' ? 'limit' : 'error'
      );
      clearMarkers();
    }
  }, [clearMarkers, editId, paramsToSave]);

  // Coming back from login with the marker still on the URL: finish the job.
  useEffect(() => {
    // Marker gone — release the claim so a later round trip can save again.
    if (!wantsAutoSave) {
      autoSaveClaim = null;
      return;
    }

    if (!isLoggedIn || status !== 'idle') return;

    const claim = `${location.pathname}${location.search}`;
    if (autoSaveClaim === claim) return;

    autoSaveClaim = claim;
    save();
  }, [wantsAutoSave, isLoggedIn, status, save, location.pathname, location.search]);

  const defaultLabel = editId ? 'Spara ändringar' : 'Bevaka denna sökning';

  // Nothing filtered yet — there is no search to watch, and the server would
  // refuse one. Say nothing rather than offer a button that cannot work.
  if (!hasSearchCriteria(location.search)) {
    return null;
  }

  if (!isLoggedIn) {
    // Send the user to login, and bring them back to this exact search with the
    // marker attached so the save happens without a second press.
    const returnParams = new URLSearchParams(location.search);
    returnParams.set(SAVE_MARKER, '1');

    const loginLabel = withIcon ? label || 'Bevaka' : 'Logga in för att bevaka';
    return (
      <NamedLink
        className={classNames(css.button, className, { [css.iconOnly]: iconOnly })}
        name="LoginPage"
        to={{ state: { from: `${location.pathname}?${returnParams.toString()}` } }}
        title={withIcon ? 'Logga in för att bevaka sökningen' : undefined}
        aria-label={iconOnly ? 'Logga in för att bevaka sökningen' : undefined}
      >
        {withIcon ? <BellIcon /> : null}
        {iconOnly ? null : loginLabel}
      </NamedLink>
    );
  }

  /**
   * Another copy of this button may have saved this search already. Show that
   * here too, rather than offering to save it a second time.
   *
   * Not while editing: there `paramsToSave()` is the bevakning being edited, so
   * it is legitimately in the saved set, and treating it as done would disable
   * the very button the user came here to press.
   */
  const savedElsewhere = !editId && status === 'idle' && savedParams.includes(paramsToSave());

  const STATUS_LABELS = {
    saving: 'Sparar…',
    saved: editId ? 'Ändringarna sparade' : 'Bevakning sparad',
    duplicate: 'Redan bevakad',
    limit: 'Max 30 bevakningar',
    error: 'Kunde inte spara',
  };

  const text = savedElsewhere
    ? 'Bevakning sparad'
    : STATUS_LABELS[status] || label || defaultLabel;

  const done = savedElsewhere || status === 'saved' || status === 'duplicate';

  return (
    <button
      type="button"
      className={classNames(css.button, className, {
        [css.buttonDone]: done,
        [css.buttonError]: status === 'error' || status === 'limit',
        [css.iconOnly]: iconOnly,
      })}
      onClick={save}
      disabled={status === 'saving' || done}
      aria-label={iconOnly ? `${text} — bevaka sökningen` : undefined}
      title={iconOnly ? text : undefined}
    >
      {withIcon ? <BellIcon /> : null}
      {iconOnly ? null : text}
    </button>
  );
};

export default SaveSearchButton;
